import React, { useState, useEffect } from 'react';
import { PostGroup, Post, UpdatePostData } from '../../services/postService';
import { useClient } from '../../contexts/ClientContext';
import { useToast } from '../../hooks/use-toast';
import postService from '../../services/postService';
// import CustomDatePicker from '../../components/ui/CustomDatePicker';
import { X, Clock, Facebook, Instagram, Twitter, Linkedin } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UpgradeModal } from '../modals/UpgradeModal';

interface ScheduleModalProps {
  postGroup: PostGroup;
  onClose: () => void;
  onSuccess: () => void;
}

const PlatformIcon = ({ platform, className }) => {
  switch (platform) {
    case 'facebook':
      return <Facebook className={className} />;
    case 'instagram':
      return <Instagram className={className} />;
    case 'twitter':
      return <Twitter className={className} />;
    case 'linkedin':
      return <Linkedin className={className} />;
    default:
      return null;
  }
};

const ScheduleModal: React.FC<ScheduleModalProps> = ({ postGroup, onClose, onSuccess }) => {
  const { currentClient } = useClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [scheduleAll, setScheduleAll] = useState<string | undefined>(undefined);
  const [individualSchedules, setIndividualSchedules] = useState<{ [postId: string]: string | undefined }>({});
  const [useScheduleAll, setUseScheduleAll] = useState(false);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalData, setUpgradeModalData] = useState<{
    message: string;
    currentCount?: number;
    limit?: number;
  }>({ message: '' });

  // Initialize with existing scheduled times
  useEffect(() => {
    // Check if post group has a scheduled time
    if (postGroup.scheduled_at) {
      // Convert UTC time to local time for datetime-local input
      const utcDate = new Date(postGroup.scheduled_at);
      const localDateTime = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setScheduleAll(localDateTime);
      setUseScheduleAll(true);
    } else {
      // Initialize individual post schedules
      const initialSchedules: { [postId: string]: string | undefined } = {};
      postGroup.posts.forEach(post => {
        if (post.scheduled_at) {
          // Convert UTC time to local time for datetime-local input
          const utcDate = new Date(post.scheduled_at);
          const localDateTime = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
          initialSchedules[post.id] = localDateTime;
        }
      });
      setIndividualSchedules(initialSchedules);
    }
  }, [postGroup]);

  const scheduleMutation = useMutation({
    mutationFn: async () => {
      if (!currentClient) throw new Error('No client selected');

      const promises: Promise<any>[] = [];

      if (useScheduleAll && scheduleAll) {
        // Convert local datetime to UTC ISO string
        const utcDateTime = new Date(scheduleAll).toISOString();
        promises.push(postService.schedulePostOrPostGroup(postGroup.id, '', utcDateTime));
      } else {
        for (const post of postGroup.posts) {
          const scheduledAt = individualSchedules[post.id];
          if (scheduledAt) {
            // Convert local datetime to UTC ISO string
            const utcDateTime = new Date(scheduledAt).toISOString();
            promises.push(postService.schedulePostOrPostGroup(postGroup.id, post.id, utcDateTime));
          }
        }
      }
      
      if (promises.length === 0) {
        throw new Error('No posts selected for scheduling');
      }

      await Promise.all(promises);
      return promises.length;
    },
    onSuccess: (count) => {
      toast({
        title: "Posts Scheduled",
        description: `${count} post${count > 1 ? 's' : ''} have been scheduled successfully.`,
      });
      
      // Invalidate all relevant queries to ensure calendar updates
      queryClient.invalidateQueries({ queryKey: ['scheduledPosts', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostGroups', currentClient?.id] });
      queryClient.invalidateQueries({ queryKey: ['scheduledPostsForQueue'] });
      queryClient.invalidateQueries({ queryKey: ['draftPosts'] });
      queryClient.invalidateQueries({ queryKey: ['postGroups'] });
      
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      console.error("Failed to schedule posts:", error);
      
      if (error.message === 'No posts selected for scheduling') {
        toast({
          title: "No Changes",
          description: "No posts were selected for scheduling.",
          variant: "destructive",
        });
      } else if (error?.response?.status === 403 && error?.response?.data?.detail) {
        const errorDetail = error.response.data.detail;
        
        // Check if it's a scheduled queue limit error
        if (typeof errorDetail === 'object' && errorDetail.error === 'scheduled_queue_limit_exceeded') {
          setUpgradeModalData({
            message: errorDetail.message,
            currentCount: errorDetail.current_count,
            limit: errorDetail.limit
          });
          setShowUpgradeModal(true);
        } else {
          const message = typeof errorDetail === 'string' ? errorDetail : errorDetail.message || 'Scheduling failed';
          toast({
            title: "Scheduling Failed",
            description: message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Scheduling Failed",
          description: "An error occurred while scheduling the posts.",
          variant: "destructive",
        });
      }
    },
  });

  const handleSchedule = () => {
    scheduleMutation.mutate();
  };

  const handleIndividualScheduleChange = (postId: string, date: string | undefined) => {
    setIndividualSchedules(prev => ({ ...prev, [postId]: date }));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-slate-900 border-slate-200/50 dark:border-slate-600/30 w-full max-w-3xl backdrop-blur-xl">
        <DialogHeader className="pb-6">
          <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span>Schedule Posts</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Schedule All Toggle */}
          <div className="relative overflow-hidden rounded-xl bg-slate-50/50 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5"></div>
            <div className="relative p-4">
              <div className="flex items-center space-x-4">
                <Switch
                  id="schedule-all-switch"
                  checked={useScheduleAll}
                  onCheckedChange={setUseScheduleAll}
                  className="data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-700"
                />
                <div className="flex-1">
                  <label htmlFor="schedule-all-switch" className="text-sm font-semibold text-gray-900 dark:text-slate-200 cursor-pointer">
                    Schedule all posts at the same time
                  </label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    All posts will be published simultaneously at the selected time
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Global Schedule Time */}
          {useScheduleAll && (
            <div className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5"></div>
              <div className="relative p-4">
                <label className="block text-sm font-semibold text-gray-900 dark:text-slate-200 mb-3">
                  Global Schedule Date & Time
                </label>
                <input
                  type="datetime-local"
                  value={scheduleAll || ''}
                  onChange={(e) => setScheduleAll(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-100/50 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/30 rounded-lg text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 text-sm"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
            </div>
          )}

          {/* Individual Posts */}
          <div className="space-y-4" style={{ opacity: useScheduleAll ? 0.5 : 1 }}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-200 flex items-center space-x-2">
              <span>Individual Posts</span>
              {useScheduleAll && (
                <span className="text-xs bg-slate-200/50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 px-2 py-1 rounded-full">
                  Disabled when scheduling all
                </span>
              )}
            </h3>
            
            <div className="grid gap-4">
              {postGroup.posts.map(post => (
                <div key={post.id} className="relative overflow-hidden rounded-xl bg-white dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200/50 dark:border-slate-600/30 shadow-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 via-gray-500/5 to-slate-500/5"></div>
                  <div className="relative p-4">
                    <div className="flex items-start space-x-4">
                      {/* Platform Icon */}
                      <div className="flex-shrink-0">
                        {post.platforms && post.platforms.length > 0 && (
                          <div className="w-10 h-10 bg-slate-100/50 dark:bg-slate-700/50 rounded-lg flex items-center justify-center">
                            <PlatformIcon platform={post.platforms[0]} className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* Post Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-semibold text-gray-900 dark:text-slate-200 truncate">
                          {post.title}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 line-clamp-2">
                          {post.content || 'No content available.'}
                        </p>
                        
                        {/* Schedule Input */}
                        <div className="mt-4">
                          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                            Schedule Date & Time
                          </label>
                          <input
                            type="datetime-local"
                            value={individualSchedules[post.id] || ''}
                            onChange={(e) => handleIndividualScheduleChange(post.id, e.target.value)}
                            disabled={useScheduleAll}
                            className="w-full px-3 py-2 bg-slate-100/50 dark:bg-slate-700/50 border border-slate-200/50 dark:border-slate-600/30 rounded-lg text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            min={new Date().toISOString().slice(0, 16)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="pt-6 border-t border-slate-200/50 dark:border-slate-600/30">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="bg-slate-100/50 hover:bg-slate-200/50 dark:bg-slate-700/50 border-slate-300/70 dark:border-slate-600/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSchedule}
            disabled={scheduleMutation.isPending}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg hover:shadow-blue-500/25 disabled:opacity-50"
          >
            {scheduleMutation.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Scheduling...
              </>
            ) : (
              <>
                <Clock className="w-4 h-4 mr-2" />
                Schedule Posts
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Scheduled Queue Limit Reached"
        message={upgradeModalData.message}
        currentCount={upgradeModalData.currentCount}
        limit={upgradeModalData.limit}
        feature="scheduled posts"
      />
    </Dialog>
  );
};

export default ScheduleModal;
